import { DateTime } from "luxon";
import React from "react";
import { action, observable, runInAction, computed } from "mobx";

import { getParamFromUrl } from "../components/url";
import { setSearchParam } from "../components/util-api";

import UserStore from "./user";
import FavoritesStore from "../stores/favoritesStore";

import {
  getPrevTab,
  getNextTab,
  goPrevHist,
  goNextHist,
  getHistFromDate,
} from "../components/util-tab";
import { BIG_GROUPS } from "../components/util-group";
import { buildSectorUrl, buildSymbolUrl } from "../components/util-url";
import {
  fetchRecentSignals,
  fetchSignalHistory as fetchSignalHistoryApi,
} from "../components/util-api";

const WEEKDAY_MAP = {
  0: "Sun",
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
};

/* Store start */
export default class Store {
  constructor() {
    this.userStore = new UserStore();

    // ✅ new
    this.favoritesStore = new FavoritesStore(this);

    this.initFromUrl();

    window.addEventListener("keydown", (e) => {
      if ((e.metaKey && e.key === "+") || (e.metaKey && e.key === "-")) {
        e.preventDefault();
        console.log("Zoom shortcuts disabled");
      }
      if (e.ctrlKey) {
        //console.log("ctrl key pressed");
        //this.toggleAllowPopover();
        if (e.keyCode === 38) {
          console.log("Up");
          e.preventDefault();
          e.stopPropagation();

          const prevTab = getPrevTab(this.activeTab);
          if (prevTab) {
            this.setFilter("");
            this.setActiveTab(prevTab);
          }
        } else if (e.keyCode === 40) {
          console.log("Down");
          e.preventDefault();
          e.stopPropagation();

          const nextTab = getNextTab(this.activeTab);
          if (nextTab) {
            this.setFilter("");
            this.setActiveTab(nextTab);
          }
        } else if (e.key === "f") {
          this.setSummaryTabStatus(!this.summaryTabStatus);
          return;
        }
      }

      /*
            if (e.key==='j') {
                this.setSummaryTabStatus(!this.summaryTabStatus)
                e.preventDefault();
                e.stopPropagation();
                return
            }
            */

      if (e.key === "Escape") {
        this.setFilterInput("");
        this.setActiveTab(null);
        this.setFilter("");
      }

      if (e.key === "Enter") {
        if (this.formattedDate !== this.formattedNavDate) {
          this.setDate(this.navDate);
          this.setData(null);
        }
      }

      if (this.allowShortCut) {
        if (e.key === "PageUp") {
          console.log("PageUp");
          this.goNextDate();
          this.setDate(this.navDate);
        } else if (e.key === "PageDown") {
          console.log("PageDown");
          this.goPrevDate();
          this.setDate(this.navDate);
        } else if (e.key === "z") {
          this.setLess(!this.less);
        } else if (e.key === "u") {
          this.setCut(!this.cut);
        } else if (e.key === "a") {
          this.setAnnotation(!this.annotation);
        } else if (e.key === "v") {
          this.setShowType("vol");
        } else if (e.key === "n") {
          this.setShowType("neg");
        } else if (e.key === "p") {
          this.setShowType("pos");
        } else if (e.key === "q") {
          this.setShowType("all");
        } else if (e.key === "s") {
          this.setShowSectorOnly(!this.showSectorOnly);
        } else if (e.key === "g") {
          this.setGroupSize(this.groupSize === 3 ? 1 : 3);
          this.setGroupOnly(!this.groupOnly);
        } else if (e.key === "h") {
          const hist = getHistFromDate(this.formattedDate);
          if (hist) {
            this.setHist(hist);
            setSearchParam("hist", hist);
          }
        } else if (e.key === "ArrowLeft") {
          const prevHist = goPrevHist(this.hist);
          console.log("P", prevHist, this.hist);
          if (prevHist) {
            this.setHist(prevHist);
            setSearchParam("hist", prevHist);
          }
        } else if (e.key === "ArrowRight") {
          const nextHist = goNextHist(this.hist);
          console.log("N", nextHist, this.hist);
          if (nextHist) {
            this.setHist(nextHist);
            setSearchParam("hist", nextHist);
            this.setCut(false);
          }
        }
        e.preventDefault();
      }
    });
  }

  @observable userStore; // Add userStore as an observable property
  @observable authUser = null;
  @observable favoritesStore; // child store

  @observable title = "Coding is Love";

  @observable user = {
    userId: 1,
    name: "Ranjith kumar",
    website: "https://codingislove.com",
    email: "ranjith@codingislove.com",
  };

  @observable isAdmin = false;
  @observable isAdminParam = false;
  @observable isPercent = true;
  @observable showPattern = true; // Show pattern text in percentage mode
  @observable percentSortDesc = true; // true = descending, false = ascending
  @observable isMarketCapFilter = false;
  @observable allowSignal = false;
  @observable allowDrillDown = false;
  @observable showER = true;
  @observable activeTab = "cluster_pos";

  // Cluster data observables
  @observable clusterData = null;
  @observable clusterErr = null;
  @observable clusterLoading = false;

  @observable signOnly = 1;

  @observable output = "";

  @observable showEROnly = false;

  @observable strongSymbolsMap = {};

  @observable showShort = "";

  @observable reportType = "";

  @observable showType = "all"; //vol

  @observable orderType = "1"; //vol

  @observable showCluster = true;

  @observable showSectorOnly = true;

  @observable date = null;

  @observable navDate = null;

  @observable count = 300;

  @observable patternSize = 0;

  @observable maxSymbolSize = 14; //Dead

  @observable groupSize = 3;

  @observable strengthLimit = 0;

  @observable cut = false;

  @observable less = false;

  @observable canMouseOver = false;

  @observable annotation = false;

  @observable sector = "etf";

  @observable symbols = "";

  @observable showingSectors = [];

  @observable showingGroups = [];

  @observable symbol = "";

  @observable showSize = 0;

  @observable filterValue = undefined;

  @observable hoveredFilterValue = undefined;

  @observable filterInput = "";

  @observable hoveredFilterInput = "";

  @observable trimInput = "";

  @observable expanded = false;

  @observable preFilter = false;

  @observable isTrim = false;

  @observable showPatternSign = false;

  @observable activeType = "sum";

  @observable activeSummary = null;

  @observable data = null;

  @observable showTable = false;

  @observable showStrongBadge = false;

  @observable allowPopover = true;

  @observable allowShortCut = false;

  @observable selectedTick = null;

  @observable fixPopover = false;

  @observable showChartSettings = false;

  @observable showColorPatOnly = false;

  @observable showTabs = false;

  @observable sortSector = false;

  @observable sortSymbol = false;

  @observable cluster = false;

  @observable groupOnly = false;

  @observable watchedSymbol = null;

  @observable chartDate = null;

  @observable showDetail = false;

  @observable hist = "";

  @observable vol = true;

  @observable chartsInRow = true;

  @observable ticksList = "";

  @observable tabTicks = [];

  @observable showSettings = false;

  @observable showAllTabs = true;

  @observable collectYear = "";
  @observable collectMonth = 1;
  @observable collectType = "";
  @observable collectFilter = "";

  @observable badgeColorOnly = false;

  @observable clusterColorOnly = true;

  @observable rangeOnly = true;

  @observable qClusterOnly = true;

  @observable allowScroll = true;

  @observable summaryTabStatus = true;

  @observable signalType = "";
  @observable watchFilter = "";

  @observable signalTabValue = 0;

  @observable signalPos = [];
  @observable signalNeg = [];

  @observable signalPosSymbols = new Set();
  @observable signalNegSymbols = new Set();

  @observable signalHistoryPos = [];
  @observable signalHistoryNeg = [];

  @computed get signalPosCount() {
    return this.signalPos.length;
  }

  @computed get signalNegCount() {
    return this.signalNeg.length;
  }

  @computed get signalCount() {
    return (this.signalPos?.length || 0) + (this.signalNeg?.length || 0);
  }

  isInSignals(sym) {
    const s = (sym || "").toLowerCase();
    return this.signalPosSymbols.has(s) || this.signalNegSymbols.has(s);
  }

  // ✅ Optimistically add a symbol into signal sets + arrays
  @action
  addSignalSymbol({ symbol, orderType, sector, signalStrength = 5 }) {
    const s = (symbol || "").toLowerCase().trim();
    if (!s) return;

    const row = { symbol: s, sector, signalStrength };

    if (orderType === 1 || orderType === "1") {
      if (!this.signalPosSymbols?.has(s)) {
        this.signalPosSymbols = new Set([...this.signalPosSymbols, s]);
      }
      // keep array in sync (so computed counts change)
      if (!this.signalPos?.some((x) => (x?.symbol || "").toLowerCase() === s)) {
        this.signalPos = [...this.signalPos, row];
      }
    } else if (orderType === -1 || orderType === "-1") {
      if (!this.signalNegSymbols?.has(s)) {
        this.signalNegSymbols = new Set([...this.signalNegSymbols, s]);
      }
      if (!this.signalNeg?.some((x) => (x?.symbol || "").toLowerCase() === s)) {
        this.signalNeg = [...this.signalNeg, row];
      }
    }
  }

  initFromUrl() {
    const sector = getParamFromUrl({ key: "sector" });
    if (sector) {
      this.setSector(sector);
    }

    const signalType = getParamFromUrl({ key: "signalType" });
    if (signalType) {
      this.setSignalType(signalType);
    }

    const orderType = getParamFromUrl({ key: "orderType" });

    if (orderType) {
      this.setOrderType(orderType);
    }

    const watchFilter = getParamFromUrl({ key: "wFilter" });
    if (watchFilter) {
      this.setWatchFilter(watchFilter);
    }

    const reportType = getParamFromUrl({ key: "reportType" });
    if (reportType) {
      this.reportType = reportType;
      this.summaryTabStatus = reportType === "pos" ? true : false;
      this.showType = reportType;
    }

    const symbols = getParamFromUrl({ key: "symbols" });
    if (symbols) {
      this.setSymbols(symbols);
    }

    const symbol = getParamFromUrl({ key: "symbol" });
    if (symbol) {
      this.setSymbol(symbol);
    }

    const count = getParamFromUrl({ key: "count" });
    if (count) {
      this.setCount(Number(count));
    }

    const signalTabValue = getParamFromUrl({ key: "tab" });
    if (signalTabValue) {
      this.setSignalTabValue(Number(signalTabValue));
    }
    const cut = getParamFromUrl({ key: "cut" });
    if (cut) {
      this.setCut(true);
    }

    const canMouseOver = getParamFromUrl({ key: "canHover" });
    if (canMouseOver) {
      this.setCanMouseOver(true);
    }

    const colorPat = getParamFromUrl({ key: "colorPat" });
    if (colorPat) {
      this.setShowColorPatOnly(colorPat);
    }

    const annotation = getParamFromUrl({ key: "annotation" });
    if (annotation) {
      this.setAnnotation(annotation);
    }

    const preFilter = getParamFromUrl({ key: "preFilter" });
    if (preFilter) {
      this.setPreFilter(true);
    } else {
      this.setPreFilter(false);
    }

    const less = getParamFromUrl({ key: "less" });
    if (less) {
      this.setLess(true);
    }

    const hist = getParamFromUrl({ key: "hist" });
    if (hist) {
      this.setHist(hist);
    }

    const vol = getParamFromUrl({ key: "vol" });
    if (vol) {
      this.setVol(true);
    }

    this.adjustTime();

    const showChartSettings = getParamFromUrl({ key: "showChartSettings" });
    //this.setShowChartSettings(showChartSettings ? true : false);
    this.showChartSettings = showChartSettings ? true : false;
  }

  @observable strongSort = true;
  @action
  toggleStrongSort() {
    this.strongSort = !this.strongSort;
  }

  @observable sectorSymbolsSortByStrength = true;
  @action
  toggleSectorSymbolsSort() {
    this.sectorSymbolsSortByStrength = !this.sectorSymbolsSortByStrength;
  }

  @observable sectorSymbolsSortBySector = false;
  @action
  toggleSectorSymbolsSortBySector() {
    this.sectorSymbolsSortBySector = !this.sectorSymbolsSortBySector;
  }

  @observable filterByPercentageThreshold = true;
  @action
  toggleFilterByPercentageThreshold() {
    this.filterByPercentageThreshold = !this.filterByPercentageThreshold;
  }

  @action
  toggleMarketCapFilter() {
    this.isMarketCapFilter = !this.isMarketCapFilter;
  }

  @observable showStockGridAsRows = true;
  @action
  toggleStockGridMode() {
    this.showStockGridAsRows = !this.showStockGridAsRows;
  }

  @observable showStockGridGrouped = true;
  @action
  toggleStockGridGrouped() {
    this.showStockGridGrouped = !this.showStockGridGrouped;
  }

  @action
  setIsAdmin(val) {
    this.isAdmin = val;
  }

  @action
  setIsPercent(val) {
    this.isPercent = val;
  }

  @action
  toggleShowPattern() {
    this.showPattern = !this.showPattern;
  }

  @action
  togglePercentSortOrder() {
    this.percentSortDesc = !this.percentSortDesc;
  }

  @action
  setIsAdminParam(val) {
    this.isAdminParam = val;
  }

  @action
  setIsMarketCapFilter(val) {
    this.isMarketCapFilter = val;
  }

  @action
  setAllowSignal(val) {
    this.allowSignal = val;
  }

  @action
  setAllowDrillDown(val) {
    this.allowDrillDown = val;
  }

  @action
  setShowER(val) {
    this.showER = val;
  }

  @action
  setActiveTab(tab) {
    this.activeTab = tab;
  }

  @action
  setClusterData(data) {
    this.clusterData = data;
  }

  @action
  setClusterErr(err) {
    this.clusterErr = err;
  }

  @action
  setClusterLoading(loading) {
    this.clusterLoading = loading;
  }

  @action
  adjustTime() {
    const dateParam = getParamFromUrl({ key: "date" });
    let dt;

    if (dateParam) {
      dt = DateTime.fromISO(dateParam, { zone: "America/New_York" });
    } else {
      dt = DateTime.now().setZone("America/New_York");
    }

    // Weekend adjustment → roll back to Friday
    if (dt.weekday === 6) {
      dt = dt.minus({ days: 1 }); // Saturday → Friday
    } else if (dt.weekday === 7) {
      dt = dt.minus({ days: 2 }); // Sunday → Friday
    }

    //this.setDate(dt, false);
    this.setDate(dt);
    this.navDate = dt;
  }

  @computed get favoriteListSorted() {
    return this.favoritesStore?.sorted || [];
  }

  // ✅ Keep backwards-compat aliases
  @computed get favoriteList() {
    return this.favoritesStore?.list || [];
  }

  @action toggleFavoriteList(sym) {
    return this.favoritesStore?.toggle(sym);
  }

  @action addToFavoriteList(sym) {
    return this.favoritesStore?.add(sym);
  }

  @action removeFromFavoriteList(sym) {
    return this.favoritesStore?.remove(sym);
  }

  @action clearFavoriteList() {
    // Clear everywhere if logged in, local only if not
    return this.authUser
      ? this.favoritesStore?.clearEverywhere()
      : this.favoritesStore?.clearLocalOnly();
  }

  @action
  setUser(user) {
    this.user = user;
  }

  @action
  updateUser(data) {
    this.user = { ...this.user, ...data };
  }

  @action
  clearUser() {
    this.user = undefined;
  }

  setAuthUser(user) {
    runInAction(() => {
      this.authUser = user || null;
    });

    const isAdminMeta = !!(
      user &&
      user.app_metadata &&
      (user.app_metadata.is_admin === true ||
        user.app_metadata.is_admin === "true")
    );

    runInAction(() => {
      this.isAdminParam = isAdminMeta;
      this.isAdmin = isAdminMeta;
    });

    if (this.favoritesStore) {
      this.favoritesStore.onAuthUserChanged(this.authUser);
    }
  }

  @action
  setTitle(title) {
    this.title = title;
  }

  @action
  setSignalTabValue(signalTabValue) {
    this.signalTabValue = signalTabValue;
    setSearchParam("tab", signalTabValue);
  }

  @action
  setChartsInRow(chartsInRow) {
    this.chartsInRow = chartsInRow;
  }

  @action
  setPreFilter(preFilter) {
    this.preFilter = preFilter;
    if (preFilter) {
      setSearchParam("preFilter", 1);
    }
  }

  @action
  setSignOnly(signOnly) {
    this.signOnly = signOnly;
    if (signOnly == 5) {
      //market
      this.showShort = "long"; //long
    }

    if (signOnly == 1) {
      //all
      //this.showSize = 10; //
    }

    if (signOnly === 12) {
      //s,v
      //this.patternSize = 1;
    }
  }

  @action
  setShowShort(showShort) {
    this.showShort = showShort;
    if (showShort === "short") {
      this.signOnly = 1;
    }
  }

  @action
  setShowType(showType) {
    this.showType = showType;
    if (showType === "vol") {
      this.showSectorOnly = false;
    }
  }

  @action
  setShowCluster(showCluster) {
    this.showCluster = showCluster;
  }

  @action
  setShowSectorOnly(showSectorOnly) {
    this.showSectorOnly = showSectorOnly;
  }

  @action
  setShowStrongBadge(showStrongBadge) {
    this.showStrongBadge = showStrongBadge;
  }

  @action
  setStrongSymbolsMap(map, merge = false) {
    if (merge && this.strongSymbolsMap) {
      // merge new symbols into existing map
      this.strongSymbolsMap = {
        ...this.strongSymbolsMap,
        ...map,
      };
    } else {
      // replace entirely
      this.strongSymbolsMap = map;
    }
  }

  @observable gridMap = null;
  @action
  setStockGridMap(gridMap) {
    this.gridMap = gridMap;
  }

  //this is Prod current
  @observable currentGridMap = {};

  // Signal context date - used for highlighting charts in signal view
  @observable signalContextDate = null;
  @action
  setSignalContextDate(date) {
    this.signalContextDate = date;
  }

  @action
  async fetchCurrentGridMap(/*basePath = "/gridMap.json"*/) {
    const date = this.formattedDate;
    if (!date) return;
    const basePath = `/pv/${date}.gridMap.json`;

    try {
      const res = await fetch(basePath, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const contentType = res.headers.get("content-type") || "";
      let data;

      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const text = await res.text();
        try {
          data = JSON.parse(text);
        } catch (err) {
          console.warn("fetchCurrentGridMap: non-JSON response", {
            basePath,
            status: res.status,
            contentType,
            preview: text.slice(0, 120),
          });
          return;
        }
      }

      runInAction(() => {
        this.currentGridMap = data || {};
        //console.log("AAAcurrentGridMap", this.currentGridMap);
      });
    } catch (e) {
      console.error("fetchCurrentGridMap error", e);
    }
  }

  @action
  async fetchClusterData({ basePath = "/cluster", formattedDate }) {
    this.setClusterLoading(true);
    this.setClusterErr(null);

    try {
      const url = `${basePath}/${formattedDate}.json`;
      const res = await fetch(url, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      this.setClusterData(data);
    } catch (error) {
      console.error("fetchClusterData error:", error);
      this.setClusterErr(error.message || "Failed to fetch cluster data");
    } finally {
      this.setClusterLoading(false);
    }
  }

  @observable marketCapMap = {};
  @action
  async fetchMarketCapMap(basePath = "/yf.csv") {
    try {
      const res = await fetch(basePath, {
        cache: "no-store",
        headers: { Accept: "text/csv" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();

      // parse csv
      const rows = window.d3.csvParse(text, (d) => ({
        symbol: d.symbol?.trim(),
        marketCap: d.marketCap ? +d.marketCap : null,
      }));

      const map = {};
      for (const row of rows) {
        if (row.symbol && row.marketCap) {
          map[row.symbol] = row.marketCap;
        }
      }

      runInAction(() => {
        this.marketCapMap = map;
        //console.log("AAAmarketCapMap", this.marketCapMap);
      });
    } catch (e) {
      console.error("fetchMarketCapMap error", e);
    }
  }

  @observable patternMap = {};

  @action
  async fetchPatternMap() {
    const date = this.formattedDate;
    const basePath = `/pv/${date}.default.csv`;

    try {
      const res = await fetch(basePath, {
        cache: "no-store",
        headers: { Accept: "text/csv" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();

      // parse CSV using d3
      const rows = window.d3.csvParse(text, (d) => ({
        symbol: d.symbol?.trim(),
        briefPattern: d.briefPattern?.trim(),
      }));

      const map = {};
      for (const row of rows) {
        if (row.symbol) {
          map[row.symbol] = row.briefPattern || null;
        }
      }

      runInAction(() => {
        this.patternMap = map;
        //console.log("AAApatternMap", rows);
      });
    } catch (e) {
      console.error("fetchPatternMap error", e);
    }
  }

  @observable erMap = {};

  @action
  async fetchErMap() {
    const date = this.formattedDate;
    const basePath = `/pv/${date}.er.csv`;
    try {
      const res = await fetch(basePath, {
        cache: "no-store",
        headers: { Accept: "text/csv" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();

      // parse CSV using d3
      const rows = window.d3.csvParse(text, (d) => ({
        symbol: d.symbol?.trim(),
        lo: d.lo ? +d.lo : null,
        close: d.close ? +d.close : null,
        strength: d.strength ? +d.strength : null,
        attr: d.attr?.trim(),
        pattern: d.pattern?.trim(),
        rsi: d.rsi ? d.rsi.trim() : null,
        vol: d.vol ? d.vol.trim() : null,
        pRatio: d.pRatio ? d.pRatio.trim() : null,
        description: d.description?.trim(),
        ch: d.ch?.trim(),
        sector: d.sector?.trim(),
        briefPattern: d.briefPattern?.trim(),
        boDur: d.boDur?.trim(),
        ov: d.ov?.trim(),
        cover: d.cover?.trim(),
        lastVolCount: d.lastVolCount?.trim(),
        er: d.er?.trim(),
        erVal: d.erVal?.trim(),
      }));

      const map = {};
      for (const row of rows) {
        if (row.symbol) {
          map[row.symbol] = row.er; // store the entire row for richer access
        }
      }

      runInAction(() => {
        this.erMap = map;
        // console.log("AAAerMap", this.erMap);
      });
    } catch (e) {
      console.error("fetchErMap error", e);
    }
  }

  @action
  async fetchStrongSymbols({ symbols, date, basePath = "/csv-adv" }) {
    const ctrl = new AbortController();
    try {
      const tasks = symbols.map(async (sym) => {
        const url = `${basePath.replace(/\/$/, "")}/${sym.toLowerCase()}.csv`;
        try {
          const res = await fetch(url, {
            signal: ctrl.signal,
            cache: "no-store",
            headers: { Accept: "text/csv" },
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const text = await res.text();
          const ticks = window.d3.csvParse(text, (d) => ({
            date: d.date || d.timestamp,
            open: d.open ? +d.open : undefined,
            high: d.high ? +d.high : undefined,
            low: d.low ? +d.low : undefined,
            close: d.close || d.adjClose ? +d.close || +d.adjClose : undefined,
            volume: d.volume ? +d.volume : undefined,
            timestamp: d.timestamp,
            marketCap: d.marketCap,
          }));
          return [sym, ticks];
        } catch {
          return [sym, []];
        }
      });

      const pairs = await Promise.all(tasks);
      const strongMap = {};

      for (const [sym, ticks] of pairs) {
        let today, prev;
        if (date && ticks.length) {
          const idx = ticks.findIndex(
            (t) => t.timestamp === date || t.date === date,
          );
          if (idx >= 0) {
            today = ticks[idx];
            prev = ticks[idx + 1];
          } else {
            today = ticks[0];
            prev = ticks[1];
          }
        } else {
          today = ticks[0];
          prev = ticks[1];
        }
        strongMap[sym] = [today, prev].filter(Boolean);
      }

      runInAction(() => {
        this.setStrongSymbolsMap(strongMap, true); // merge
      });
    } catch (e) {
      if (!ctrl.signal.aborted) console.error("fetchStrongSymbols error", e);
    }
  }

  @action
  toggleShowTabs() {
    this.showTabs = !this.showTabs;
  }

  @action
  setDate(dt, syncUrl = true) {
    // 强制转为纽约时区
    this.date = dt.setZone("America/New_York");

    this.filterValue = undefined;
    this.filterInput = "";
    this.fixPopover = false;
    this.activeSummary = "";

    if (syncUrl && this.date) {
      setSearchParam("date", this.date.toISODate()); // YYYY-MM-DD
    }
  }

  /** 导航日期 */
  @action
  goDate(dt) {
    this.navDate = dt.setZone("America/New_York");
  }

  /** 从 URL 初始化/或取当前时间，回退到周五 */
  @action
  adjustTime() {
    const dateParam = getParamFromUrl({ key: "date" });
    let dt;

    if (dateParam) {
      dt = DateTime.fromISO(dateParam, { zone: "America/New_York" });
    } else {
      dt = DateTime.now().setZone("America/New_York");
    }

    // Weekend adjustment → roll back to Friday
    if (dt.weekday === 6) {
      dt = dt.minus({ days: 1 }); // Saturday → Friday
    } else if (dt.weekday === 7) {
      dt = dt.minus({ days: 2 }); // Sunday → Friday
    }

    this.setDate(dt, false);
    this.navDate = dt;
  }

  /** 前一天 */
  @action
  goPrevDate() {
    if (this.date) {
      this.setDate(this.date.minus({ days: 1 }));
    }
  }

  /** 后一天 */
  @action
  goNextDate() {
    if (this.date) {
      this.setDate(this.date.plus({ days: 1 }));
    }
  }

  /** 前一周 */
  @action
  goPrevWeek() {
    if (this.date) {
      this.setDate(this.date.minus({ weeks: 1 }));
    }
  }

  /** 后一周 */
  @action
  goNextWeek() {
    if (this.date) {
      this.setDate(this.date.plus({ weeks: 1 }));
    }
  }

  /** 前一月 */
  @action
  goPrevMonth() {
    if (this.date) {
      this.setDate(this.date.minus({ months: 1 }));
    }
  }

  /** 后一月 */
  @action
  goNextMonth() {
    if (this.date) {
      this.setDate(this.date.plus({ months: 1 }));
    }
  }

  /** 前一季度 */
  @action
  goPrevQuarter() {
    if (this.date) {
      this.setDate(this.date.minus({ months: 3 }));
    }
  }

  /** 后一季度 */
  @action
  goNextQuarter() {
    if (this.date) {
      this.setDate(this.date.plus({ months: 3 }));
    }
  }

  /** 前一年 */
  @action
  goPrevYear() {
    if (this.date) {
      this.setDate(this.date.minus({ years: 1 }));
    }
  }

  /** 后一年 */
  @action
  goNextYear() {
    if (this.date) {
      this.setDate(this.date.plus({ years: 1 }));
    }
  }

  @action
  setCount(count) {
    this.count = count;
    setSearchParam("count", count);
  }

  @action
  setChartDate(date) {
    this.chartDate = date;
  }

  @action
  setGroupOnly(groupOnly) {
    this.groupOnly = groupOnly;
  }

  @action
  setShowEROnly(showEROnly) {
    this.showEROnly = showEROnly;
  }

  @action
  setPatternSize(size) {
    this.patternSize = size;
  }

  @action
  setMaxSymbolSize(size) {
    this.maxSymbolSize = size;
  }

  @action
  setGroupSize(size) {
    this.groupSize = size;
  }

  @action
  setBadgeColorOnly(only) {
    this.badgeColorOnly = only;
  }

  @action
  setClusterColorOnly(only) {
    this.clusterColorOnly = only;
  }

  @action
  setRangeOnly(only) {
    this.rangeOnly = only;
  }

  @action
  setQClusterOnly(only) {
    this.qClusterOnly = only;
  }

  @action
  setStrengthLimit(strengthLimit) {
    this.strengthLimit = strengthLimit;
    //setSearchParam("strengthLimit", strengthLimit);
  }

  @action
  setCut(cut) {
    this.cut = Boolean(cut);
    setSearchParam("cut", cut ? "1" : "");
  }

  @action
  setCanMouseOver(can) {
    this.canMouseOver = can;
  }

  @action
  setLess(less) {
    this.less = less;
    setSearchParam("less", less ? "1" : "");
  }

  @action
  setHist(hist) {
    this.hist = hist;
  }

  @action
  setVol(vol) {
    this.vol = vol;
  }

  @action
  setAnnotation(annotation) {
    this.annotation = annotation;
    setSearchParam("annotation", annotation ? "1" : "");
  }

  @action
  setSector(sector) {
    this.sector = sector;
    //setSearchParam("sector", sector);
  }

  @action
  setSignalType(signalType) {
    this.signalType = signalType;
  }

  @action
  setOrderType(orderType) {
    this.orderType = orderType;
  }

  @action
  setWatchFilter(watchFilter) {
    this.watchFilter = watchFilter;
  }

  @action
  setSymbols(symbols) {
    this.symbols = symbols;
  }

  @action
  setShowingSectors(value) {
    this.showingSectors = value;
  }

  @action
  setShowingGroups(value) {
    this.showingGroups = value;
  }

  @action
  setSymbol(symbol) {
    this.symbol = symbol;
    //setSearchParam("symbol", symbol);
  }

  @action
  setSortSector(sortSector) {
    this.sortSector = sortSector;
  }

  @action
  setSortSymbol(sortSymbol) {
    this.sortSymbol = sortSymbol;
    //setSearchParam("sortSymbol", sortSymbol ? "1" : "");
  }

  @action
  setCluster(cluster) {
    this.cluster = cluster;
    //setSearchParam("cluster", cluster ? "1" : "");
  }

  @action
  setSize(size) {
    this.showSize = size;
  }

  @action
  setShowColorPatOnly(flag) {
    this.showColorPatOnly = flag;
    //setSearchParam("colorPat", flag ? "1" : "");
  }

  @action
  setAllowShortCut(allowShortCut) {
    this.allowShortCut = allowShortCut;
  }

  @action
  setSelectedTick(tick) {
    this.selectedTick = tick;
  }

  @action
  setShowDetail(showDetail) {
    this.showDetail = showDetail;
  }

  @action
  toggleShowSettings() {
    this.showSettings = !this.showSettings;
  }

  @action
  toggleShowAllTabs() {
    this.showAllTabs = !this.showAllTabs;
  }

  @action
  setFilter(filterValue) {
    if (
      filterValue &&
      this.filterValue &&
      filterValue.name === this.filterValue.name &&
      filterValue.val === this.filterValue.val
    ) {
      this.filterValue = undefined;
      return;
    }
    this.filterValue = filterValue;
  }

  @action
  setHoveredFilter(filterValue) {
    if (
      filterValue &&
      this.hoveredFilterValue &&
      filterValue.name === this.hoveredFilterValue.name &&
      filterValue.val === this.hoveredFilterValue.val
    ) {
      this.hoveredFilterInput = undefined;
      return;
    }
    this.hoveredFilterValue = filterValue;
  }

  @action
  setFilterInput(filterInput) {
    this.filterInput = filterInput;
  }

  @action
  setHoveredFilterInput(filterInput) {
    this.hoveredFilterInput = filterInput;
  }

  @action
  setTrimInput(trimInput) {
    this.trimInput = trimInput;
  }

  @action
  setExpanded(expanded) {
    this.expanded = expanded;
    //setSearchParam("expanded", expanded ? "1" : "");
    this.filterValue = undefined;
    this.filterInput = "";
    this.trimInput = "";
    if (expanded) {
      this.isTrim = false;
      //this.patternSize = PATTERN_SIZE;
    }
  }

  @action
  setIsTrim(isTrim) {
    this.isTrim = isTrim;
  }

  @action
  setActiveTab(name) {
    this.activeTab = name;
    this.activeSummary = null;
  }

  @action
  setActiveType(type) {
    this.activeType = type;
  }

  @action
  setActiveSummary(name) {
    this.activeSummary = name;
  }

  @action
  setAllowScroll(flag) {
    this.allowScroll = flag;
  }

  @action
  setSummaryTabStatus(flag) {
    if (!this.reportType) {
      this.summaryTabStatus = flag;
    }
  }

  @action
  setData(data) {
    this.data = data;
  }

  @action
  setShowTable(showTable) {
    this.showTable = showTable;
  }

  @action
  setOutput(output) {
    this.output = output;
  }

  @action
  toggleAllowPopover() {
    this.allowPopover = !this.allowPopover;
  }

  @action
  setFixPopover(fixPopover) {
    this.fixPopover = fixPopover;
  }

  @action
  setShowChartSettings(showChartSettings) {
    this.showChartSettings = showChartSettings;
  }

  @action
  markWatched(options) {
    this.watchedSymbol = options.symbol;
  }

  @action
  setShowPatternSign(showPatternSign) {
    this.showPatternSign = showPatternSign;
  }

  @action
  setTicksList(ticksList) {
    this.ticksList = ticksList;
  }

  @action
  setTabTicks(tabTicks) {
    this.tabTicks = tabTicks;
  }

  /** Getter: 格式化为 YYYY-MM-DD */
  get formattedDate() {
    return this.date ? this.date.setZone("America/New_York").toISODate() : "";
  }

  get formattedNavDate() {
    return this.navDate
      ? this.navDate.setZone("America/New_York").toISODate()
      : "";
  }

  /** Getter: 返回 JS Date (仅给 React 组件用) */
  get jsDate() {
    return this.date ? this.date.toJSDate() : null;
  }

  get jsNavDate() {
    return this.navDate ? this.navDate.toJSDate() : null;
  }

  /** 星期几 (纽约时区) */
  get dayNavDate() {
    return this.navDate ? WEEKDAY_MAP[this.navDate.weekday] : "";
  }

  /*
  get dayNavDate() {
    const day = this.navDate.getDay();
    return WEEKDAY_MAP[day];
  }
  */
  get showingGroupSectors() {
    return this.showingGroups.map((g) => BIG_GROUPS[g]).flat();
  }

  get showVolOnly() {
    return this.signOnly === 12;
  }

  getUrlParams(overrides = {}) {
    const date = overrides.date ?? this.formattedDate;
    const params = {
      less: this.less,
      cut: this.cut,
      date,
      annotation: this.annotation,
      count: this.count,
      vol: this.vol,
      hist: this.hist,
      ...overrides,
    };
    Object.keys(params).forEach((k) => {
      if (params[k] === undefined) delete params[k];
    });
    return params;
  }

  getSymbolUrl(symbol, overrides = {}) {
    if (!symbol) return "";
    return buildSymbolUrl({ symbol, ...this.getUrlParams(overrides) });
  }

  // (Optional) sector variant for convenience
  getSectorUrl(sector, overrides = {}) {
    if (!sector) return "";
    return buildSectorUrl({ sector, ...this.getUrlParams(overrides) });
  }

  @action
  async fetchSignals(orderType, date) {
    const data = await fetchRecentSignals(orderType, date);
    const sigs = data?.signals || [];

    const symbols = sigs.map((s) => s.symbol?.toLowerCase()).filter(Boolean);
    const set = new Set(symbols);

    if (orderType === "1") {
      this.signalPos = sigs;
      this.signalPosSymbols = set;
    } else if (orderType === "-1") {
      this.signalNeg = sigs;
      this.signalNegSymbols = set;
    }

    return data;
  }

  @action
  async fetchSignalHistory(orderType) {
    const data = await fetchSignalHistoryApi({
      orderType,
      includeClosed: true,
      last90days: false,
      recentLimit: 300,
    });
    const sigs = data?.signals || [];

    if (orderType === "1") {
      this.signalHistoryPos = sigs;
    } else if (orderType === "-1") {
      this.signalHistoryNeg = sigs;
    }

    return data;
  }

  getSignalHistoryForSymbol(symbol) {
    const s = symbol?.toLowerCase();

    const all = [
      ...this.signalHistoryPos.map((x) => ({ ...x, type: "Buy" })),
      ...this.signalHistoryNeg.map((x) => ({ ...x, type: "Sell" })),
    ];

    const currentDate = new Date(this.formattedDate);
    const sixtyDaysAgo = new Date(currentDate);
    sixtyDaysAgo.setDate(currentDate.getDate() - 60);

    return all
      .filter((x) => x.symbol?.toLowerCase() === s)
      .filter((x) => {
        if (!x.date) return false;
        const signalDate = new Date(x.date);
        // Filter out signals later than formattedDate or earlier than 60 days ago
        return signalDate < currentDate && signalDate >= sixtyDaysAgo;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }
}
/* Store end */

/* Store helpers */
const StoreContext = React.createContext();

export const StoreProvider = ({ children, store }) => {
  // ✅ expose globally for debugging
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.store = store; // now accessible in console
      document.store = store; // optional
      console.log("🟢 MobX store exposed as window.store");
    }
  }, [store]);

  return (
    <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
  );
};

/* Hook to use store in any functional component */
export const useStore = () => React.useContext(StoreContext);

/* HOC to inject store to any functional or class component */
export const withStore = (Component) => (props) => {
  return <Component {...props} store={useStore()} />;
};
